import { Link } from 'react-router-dom'
import { Shield, ArrowLeft } from 'lucide-react'

export default function Privacy() {
  return (
    <div className="min-h-screen bg-zenna-cream dark:bg-slate-900">
      {/* Header */}
      <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-600 dark:text-slate-400 hover:text-zenna-violet transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm font-medium">Back to Zenna</span>
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-zenna flex items-center justify-center">
              <span className="text-white font-bold text-sm">Z</span>
            </div>
            <span className="font-display font-semibold text-slate-800 dark:text-white">Zenna</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-8 md:p-12">
          {/* Title */}
          <div className="flex items-center gap-4 mb-8">
            <div className="p-3 bg-zenna-violet/10 rounded-xl">
              <Shield className="w-8 h-8 text-zenna-violet" />
            </div>
            <div>
              <h1 className="text-3xl font-display font-bold text-slate-900 dark:text-white">
                Privacy Policy
              </h1>
              <p className="text-slate-500 dark:text-slate-400 mt-1">
                Last Updated: February 8, 2026
              </p>
            </div>
          </div>

          {/* Policy Content */}
          <div className="prose prose-slate dark:prose-invert max-w-none">
            <p className="text-lg text-slate-600 dark:text-slate-300 leading-relaxed">
              ZenFlow Studios LLC ("we", "us", "our") operates Zenna Go AI at zennago.com ("Service"). 
              This Privacy Policy explains how we collect, use, and protect your information.
            </p>

            <hr className="my-8 border-slate-200 dark:border-slate-700" />

            {/* Section 1 */}
            <section className="mb-8">
              <h2 className="text-xl font-display font-semibold text-slate-900 dark:text-white mb-4">
                1. Information We Collect
              </h2>
              
              <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mt-4 mb-2">
                Account Information
              </h3>
              <p className="text-slate-600 dark:text-slate-300">
                When you sign up via Google OAuth, we receive your name and email address. 
                We use this to create your account and communicate with you.
              </p>

              <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mt-4 mb-2">
                Usage Data
              </h3>
              <p className="text-slate-600 dark:text-slate-300">
                We collect information about how you use Zenna, including designs created, 
                features used, and session duration. This helps us improve the Service.
              </p>

              <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mt-4 mb-2">
                Generated Content
              </h3>
              <p className="text-slate-600 dark:text-slate-300">
                Images and designs you create are stored in our database to enable saving 
                and loading projects. You retain ownership of your creations.
              </p>

              <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mt-4 mb-2">
                Payment Information
              </h3>
              <p className="text-slate-600 dark:text-slate-300">
                Payments are processed securely by Stripe. We do not store your credit card 
                details on our servers.
              </p>
            </section>

            {/* Section 2 */}
            <section className="mb-8">
              <h2 className="text-xl font-display font-semibold text-slate-900 dark:text-white mb-4">
                2. How We Use Your Information
              </h2>
              <ul className="list-disc list-inside space-y-2 text-slate-600 dark:text-slate-300">
                <li>To provide and maintain the Service</li>
                <li>To process your transactions and manage your subscription</li>
                <li>To send you updates about your account and important notices</li>
                <li>To improve our Service based on usage patterns</li>
                <li>To post designs to social media (only with your explicit permission)</li>
                <li>To respond to your support requests</li>
              </ul>
            </section>

            {/* Section 3 */}
            <section className="mb-8">
              <h2 className="text-xl font-display font-semibold text-slate-900 dark:text-white mb-4">
                3. Sharing Your Information
              </h2>
              <p className="text-slate-600 dark:text-slate-300 mb-4">
                <strong>We do not sell your personal information.</strong> We may share data 
                with the following service providers who help us operate Zenna:
              </p>
              <ul className="list-disc list-inside space-y-2 text-slate-600 dark:text-slate-300">
                <li><strong>Stripe</strong> — Payment processing</li>
                <li><strong>Supabase</strong> — Database hosting and authentication</li>
                <li><strong>Replicate</strong> — AI image generation</li>
                <li><strong>Anthropic</strong> — AI assistant (Claude)</li>
                <li><strong>DigitalOcean</strong> — Server hosting</li>
                <li><strong>Social media platforms</strong> — Only content you choose to share</li>
              </ul>
            </section>

            {/* Section 4 */}
            <section className="mb-8">
              <h2 className="text-xl font-display font-semibold text-slate-900 dark:text-white mb-4">
                4. Data Retention
              </h2>
              <p className="text-slate-600 dark:text-slate-300">
                Your data is retained as long as your account is active. If you delete your 
                account, we will remove your personal data within 30 days, except where we 
                are required to retain it for legal purposes. You may request deletion at 
                any time by emailing{' '}
                <a href="mailto:support@zennago.com" className="text-zenna-violet hover:underline">
                  support@zennago.com
                </a>.
              </p>
            </section>

            {/* Section 5 */}
            <section className="mb-8">
              <h2 className="text-xl font-display font-semibold text-slate-900 dark:text-white mb-4">
                5. Cookies
              </h2>
              <p className="text-slate-600 dark:text-slate-300">
                We use essential cookies for authentication and session management. These 
                cookies are necessary for the Service to function and cannot be disabled. 
                We do not use tracking cookies for advertising purposes.
              </p>
            </section>

            {/* Section 6 */}
            <section className="mb-8">
              <h2 className="text-xl font-display font-semibold text-slate-900 dark:text-white mb-4">
                6. Your Rights
              </h2>
              <p className="text-slate-600 dark:text-slate-300 mb-4">
                You have the right to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-slate-600 dark:text-slate-300">
                <li><strong>Access</strong> — Request a copy of your personal data</li>
                <li><strong>Correct</strong> — Update inaccurate information</li>
                <li><strong>Delete</strong> — Request deletion of your account and data</li>
                <li><strong>Export</strong> — Download your designs and data</li>
                <li><strong>Opt-out</strong> — Unsubscribe from marketing emails</li>
              </ul>
              <p className="text-slate-600 dark:text-slate-300 mt-4">
                To exercise these rights, contact us at{' '}
                <a href="mailto:support@zennago.com" className="text-zenna-violet hover:underline">
                  support@zennago.com
                </a>.
              </p>
            </section>

            {/* Section 7 */}
            <section className="mb-8">
              <h2 className="text-xl font-display font-semibold text-slate-900 dark:text-white mb-4">
                7. Data Security
              </h2>
              <p className="text-slate-600 dark:text-slate-300">
                We implement industry-standard security measures to protect your data, 
                including encryption in transit (HTTPS), secure database access controls, 
                and regular security audits. However, no method of transmission over the 
                Internet is 100% secure.
              </p>
            </section>

            {/* Section 8 */}
            <section className="mb-8">
              <h2 className="text-xl font-display font-semibold text-slate-900 dark:text-white mb-4">
                8. Children's Privacy
              </h2>
              <p className="text-slate-600 dark:text-slate-300">
                Zenna is not intended for children under 13 years of age. We do not knowingly 
                collect personal information from children under 13. If you believe we have 
                collected such information, please contact us immediately.
              </p>
            </section>

            {/* Section 9 */}
            <section className="mb-8">
              <h2 className="text-xl font-display font-semibold text-slate-900 dark:text-white mb-4">
                9. Changes to This Policy
              </h2>
              <p className="text-slate-600 dark:text-slate-300">
                We may update this Privacy Policy from time to time. We will notify you of 
                significant changes by email or by posting a notice on our website. Your 
                continued use of the Service after changes constitutes acceptance of the 
                updated policy.
              </p>
            </section>

            {/* Section 10 */}
            <section className="mb-8">
              <h2 className="text-xl font-display font-semibold text-slate-900 dark:text-white mb-4">
                10. Contact Us
              </h2>
              <p className="text-slate-600 dark:text-slate-300 mb-4">
                If you have any questions about this Privacy Policy, please contact us:
              </p>
              <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-6">
                <p className="font-semibold text-slate-900 dark:text-white mb-2">
                  ZenFlow Studios LLC
                </p>
                <p className="text-slate-600 dark:text-slate-300">
                  Email:{' '}
                  <a href="mailto:support@zennago.com" className="text-zenna-violet hover:underline">
                    support@zennago.com
                  </a>
                </p>
                <p className="text-slate-600 dark:text-slate-300">
                  Website:{' '}
                  <a href="https://zennago.com" className="text-zenna-violet hover:underline">
                    https://zennago.com
                  </a>
                </p>
              </div>
            </section>
          </div>
        </div>

        {/* Footer Links */}
        <div className="mt-8 text-center">
          <div className="flex items-center justify-center gap-6 text-sm text-slate-500 dark:text-slate-400">
            <Link to="/terms" className="hover:text-zenna-violet transition-colors">
              Terms of Service
            </Link>
            <span>•</span>
            <Link to="/" className="hover:text-zenna-violet transition-colors">
              Back to Zenna
            </Link>
          </div>
        </div>
      </main>
    </div>
  )
}